export const PROTOCOL_VERSION = 1 as const;

export const PROTOCOL_LIMITS = {
  messageIdCharacters: 64,
  negotiationIdCharacters: 64,
  sdpCharacters: 196_608,
  iceCandidateCharacters: 8_192,
  messageCharacters: 262_144,
  iceServers: 8,
  iceServerUrls: 16,
  iceServerUrlCharacters: 2_048,
  iceServerCredentialCharacters: 1_024,
} as const;

export const ROOM_DURATION_LIMITS = {
  minimumSeconds: 10,
  maximumSeconds: 24 * 60 * 60,
} as const;

export type ProtocolVersion = typeof PROTOCOL_VERSION;
export type PeerRole = 'host' | 'viewer';

export interface IceServerPayload {
  readonly urls: readonly string[];
  readonly username?: string;
  readonly credential?: string;
}

export type RoomCredential =
  | { readonly type: 'host-token'; readonly value: string }
  | { readonly type: 'pin'; readonly value: string }
  | { readonly type: 'invite-token'; readonly value: string }
  | { readonly type: 'resume-token'; readonly value: string };

export interface ProtocolPayloads {
  'room.create': { readonly durationSeconds: number | null };
  'room.created': {
    readonly roomCode: string;
    readonly pin: string;
    readonly inviteUrl: string;
    readonly hostToken: string;
    readonly expiresAt: string | null;
  };
  'room.join': {
    readonly roomCode: string;
    readonly role: PeerRole;
    readonly credential: RoomCredential;
  };
  'room.joined': {
    readonly role: PeerRole;
    readonly expiresAt: string | null;
    readonly resumeToken: string;
    readonly reconnected: boolean;
    readonly locked: boolean;
    readonly iceServers: readonly IceServerPayload[];
    readonly iceServersExpiresAt: string | null;
  };
  'room.lock': Record<string, never>;
  'room.unlock': Record<string, never>;
  'room.error': { readonly code: RoomErrorCode; readonly retryAfterMs?: number };
  'peer.offer': { readonly negotiationId: string; readonly sdp: string };
  'peer.answer': { readonly negotiationId: string; readonly sdp: string };
  'peer.ice': {
    readonly negotiationId: string;
    readonly candidate: IceCandidatePayload | null;
  };
  'peer.ready': { readonly role: PeerRole };
  'peer.leave': { readonly reason: PeerLeaveReason };
  'host.kick': Record<string, never>;
  'turn.refresh': Record<string, never>;
  'turn.credentials': {
    readonly iceServers: readonly IceServerPayload[];
    readonly expiresAt: string | null;
  };
  heartbeat: { readonly sentAt: number };
  error: { readonly code: ProtocolErrorCode };
}

export interface IceCandidatePayload {
  readonly candidate: string;
  readonly sdpMLineIndex: number | null;
  readonly sdpMid: string | null;
  readonly usernameFragment?: string | null;
}

export type RoomErrorCode =
  | 'ROOM_NOT_FOUND'
  | 'ROOM_EXPIRED'
  | 'ROOM_LOCKED'
  | 'WRONG_PIN'
  | 'ROOM_FULL'
  | 'RATE_LIMITED'
  | 'VIEWER_REMOVED';

export type ProtocolErrorCode =
  | 'INVALID_MESSAGE'
  | 'MESSAGE_TOO_LARGE'
  | 'UNSUPPORTED_VERSION'
  | 'AUTH_REQUIRED'
  | 'UNAUTHORIZED'
  | 'ROLE_FORBIDDEN'
  | 'INTERNAL_ERROR';

export type PeerLeaveReason = 'left' | 'kicked' | 'host-ended' | 'session-expired';

export type ProtocolMessageType = keyof ProtocolPayloads;

export type ProtocolMessage<Type extends ProtocolMessageType = ProtocolMessageType> = {
  readonly v: ProtocolVersion;
  readonly type: Type;
  readonly id: string;
  readonly epoch: number;
  readonly payload: ProtocolPayloads[Type];
};

export type AnyProtocolMessage = {
  [Type in ProtocolMessageType]: ProtocolMessage<Type>;
}[ProtocolMessageType];
